import Accordion, { type AccordionProps } from '@allesauto/core/accordion';
import TextLink from '@allesauto/core/textLink';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { getFormattedDeliveryDate } from '@allesauto/utils/funcs.ts';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

interface ExtrasConditionProps {
  comfort?: SchemaTypes.VehicleDetail['comfort'];
  security?: SchemaTypes.VehicleDetail['security'];
  entertainment?: SchemaTypes.VehicleDetail['entertainment'];
  interior?: SchemaTypes.VehicleDetail['interior'];
  exterior?: { bodyColor: string };
  sellerVehicleInfo?: string | null;
  vehicleHistory?: SchemaTypes.VehicleDetail['vehicleHistory'];
  attributeGroups?:
    | SchemaTypes.Maybe<SchemaTypes.Maybe<SchemaTypes.AttributeGroup>[]>
    | undefined;
  deliveryDate?: SchemaTypes.Vehicle['deliveryDate'];
  guaranteeUrl?: SchemaTypes.Maybe<string>;
}

const ExtrasCondition: FC<React.PropsWithChildren<ExtrasConditionProps>> = ({
  comfort,
  entertainment,
  interior,
  exterior,
  security,
  sellerVehicleInfo,
  vehicleHistory,
  attributeGroups,
  deliveryDate,
  guaranteeUrl,
}) => {
  const intl = useIntl();
  const attributeGroupsPresent = attributeGroups && attributeGroups?.length > 0;
  const deliveryDateDisplay = getFormattedDeliveryDate(deliveryDate);

  const formatAccordionItems = (
    attributes:
      | SchemaTypes.VehicleDetail['comfort']
      | SchemaTypes.VehicleDetail['interior'],
    engUpperCaseName: string,
    engName: string,
    gerName: string
  ) => {
    return Array.isArray(attributes)
      ? {
          _uid: attributeGroupsPresent
            ? intl.locale === 'de'
              ? gerName
              : engName
            : translate(intl, `ui.PDP.EXTRAS_CONDITION.${engUpperCaseName}`),
          title: attributeGroupsPresent
            ? intl.locale === 'de'
              ? gerName
              : engName
            : translate(intl, `ui.PDP.EXTRAS_CONDITION.${engUpperCaseName}`),
          children: (
            <ul>
              {attributes.map(
                (item, index) =>
                  item && (
                    <li key={item + index} id={`listItem--${engName}-${index}`}>
                      {item}
                    </li>
                  )
              )}
            </ul>
          ),
        }
      : {
          _uid: translate(intl, 'ui.PDP.EXTRAS_CONDITION.INTERIOR'),
          title: translate(intl, 'ui.PDP.EXTRAS_CONDITION.INTERIOR'),
          children: (
            <ul>
              {attributes?.interiorType && (
                <li id={`listItem--${engName}-0`}>
                  {translate(intl, 'ui.PDP.EXTRAS_CONDITION.INTERIOR_TYPE') +
                    ': ' +
                    attributes.interiorType}
                </li>
              )}
              {attributes?.interiorColor && (
                <li id={`listItem--${engName}-1`}>
                  {translate(intl, 'ui.PDP.EXTRAS_CONDITION.INTERIOR_COLOR') +
                    ': ' +
                    attributes.interiorColor}
                </li>
              )}
              {exterior?.bodyColor && (
                <li id={`listItem--${engName}-1`}>
                  {translate(intl, 'ui.PDP.EXTRAS_CONDITION.BODY_COLOR') +
                    ': ' +
                    exterior?.bodyColor}
                </li>
              )}
            </ul>
          ),
        };
  };

  const accordionEquipmentsItems: AccordionProps['items'] = [];
  const accordionAttributeGroupsItems: AccordionProps['items'] = [];

  if (comfort) {
    accordionEquipmentsItems.push(
      formatAccordionItems(comfort, 'COMFORT', 'comfort', '')
    );
  }

  if (security) {
    accordionEquipmentsItems.push(
      formatAccordionItems(security, 'SECURITY', 'security', '')
    );
  }

  if (entertainment) {
    accordionEquipmentsItems.push(
      formatAccordionItems(entertainment, 'ENTERTAINMENT', 'entertainment', '')
    );
  }

  if (interior) {
    accordionEquipmentsItems.push(
      formatAccordionItems(interior, 'INTERIOR', 'interior', '')
    );
  }

  if (sellerVehicleInfo) {
    accordionEquipmentsItems.push({
      _uid: translate(intl, 'ui.PDP.C2C.SELLER_INFO'),
      title: translate(intl, 'ui.PDP.C2C.SELLER_INFO'),
      childHtml: sellerVehicleInfo,
    });
  }

  const getGuaranteeAndDeliveryDate = useMemo(() => {
    return (
      vehicleHistory: SchemaTypes.Maybe<string>[] | undefined,
      deliveryDateDisplay: string | JSX.Element
    ): SchemaTypes.Maybe<SchemaTypes.Maybe<string | JSX.Element>[]> => {
      const guaranteeInfo = vehicleHistory?.find((info) =>
        info?.startsWith('Garantie:')
      );
      let guaranteeAccordionItem;

      const ensureAbsoluteUrl = (url: string) => {
        if (/^https?:\/\//i.test(url)) {
          return url;
        }
        return `https://${url}`;
      };

      if (guaranteeInfo) {
        if (guaranteeInfo.includes('Garantie inkl.') && guaranteeUrl) {
          guaranteeAccordionItem = (
            <TextLink
              href={ensureAbsoluteUrl(guaranteeUrl)}
              target="_blank"
              text={translate(intl, 'ui.PDP.DEALER_DETAILS.GUARANTEE_URL')}
              icon={false}
              customClassName="translate-y-[-8px]"
            />
          );
        } else {
          guaranteeAccordionItem = guaranteeInfo;
        }
      }

      const formattedDeliveryDateDisplay = `${translate(
        intl,
        'ui.PDP.EXTRAS_CONDITION.AVAILABLE_FROM'
      )} ${deliveryDateDisplay}`;

      const filteredVehicleHistory =
        vehicleHistory?.filter((info) => !info?.startsWith('Garantie:')) || [];

      const combinedVehicleHistory = [...filteredVehicleHistory];
      if (guaranteeAccordionItem) {
        combinedVehicleHistory.push(guaranteeAccordionItem as any);
      }
      combinedVehicleHistory.push(formattedDeliveryDateDisplay);

      return combinedVehicleHistory;
    };
  }, [guaranteeUrl, intl]);

  const statusItems = vehicleHistory
    ? attributeGroupsPresent
      ? [
          Object.assign(
            formatAccordionItems(
              getGuaranteeAndDeliveryDate(
                vehicleHistory,
                deliveryDateDisplay
              ) as
                | SchemaTypes.Maybe<string>[]
                | SchemaTypes.Interior
                | null
                | undefined,
              'VEHICLE_HISTORY',
              'vehicle-history',
              ''
            ),
            {
              title:
                intl.locale === 'de' ? 'Fahrzeughistorie' : 'Vehicle history',
              _uid: 'fahrzeughistorie',
            }
          ),
        ]
      : [
          formatAccordionItems(
            getGuaranteeAndDeliveryDate(vehicleHistory, deliveryDateDisplay) as
              | SchemaTypes.Maybe<string>[]
              | SchemaTypes.Interior
              | null
              | undefined,
            'VEHICLE_HISTORY',
            'vehicle-history',
            ''
          ),
        ]
    : [];

  if (attributeGroupsPresent) {
    attributeGroups.forEach((obj) => {
      if (obj && obj.attributes && obj.key && obj.name) {
        const { attributes, key, name } = obj;
        const engUpperCaseName = key.toUpperCase();

        accordionAttributeGroupsItems.push(
          formatAccordionItems(attributes, engUpperCaseName, key, name)
        );
      }
    });
  }

  return (
    <div className="col-end-24 col-start-2">
      {attributeGroupsPresent ? (
        <div className="pb-100px" data-style-for-print="accordion-container">
          <Accordion
            isMultiple={true}
            heading={translate(intl, 'ui.PDP.EXTRAS_CONDITION.EQUIPMENT')}
            items={accordionAttributeGroupsItems}
            typeTestId="equipment"
            component={''}
            _uid={''}
          />
        </div>
      ) : (
        <div className="pb-100px" data-style-for-print="accordion-container">
          <Accordion
            isMultiple={true}
            heading={translate(intl, 'ui.PDP.EXTRAS_CONDITION.EQUIPMENT')}
            items={accordionEquipmentsItems}
            typeTestId="equipment"
            component={''}
            _uid={''}
          />
        </div>
      )}
      <div className="pb-100px" data-style-for-print="accordion-container">
        <Accordion
          isMultiple={true}
          heading={translate(intl, 'ui.PDP.EXTRAS_CONDITION.STATUS')}
          items={statusItems}
          typeTestId="condition"
          component={''}
          _uid={''}
        />
      </div>
    </div>
  );
};

export default ExtrasCondition;
